/**
 * Enter-to-send for the Ask Gemini chat inputs (SE-8646).
 * @remarks
 * Both chat inputs are multiline, so on a web keyboard Enter used to insert a
 * newline and nothing sent until the scout reached for the Send button. On web,
 * Enter now sends and Shift+Enter keeps its newline.
 *
 * This is a capability gate, not a layout decision: react-native-web hands
 * `onKeyPress` the DOM KeyboardEvent (with `shiftKey` / `isComposing`), while
 * native multiline inputs report Return as `"Enter"` with no modifier state, so
 * applying it there would make Return send and leave no way to type a newline.
 */
import { Platform } from "react-native";

import type {
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
} from "react-native";

/**
 * Key-press fields react-native-web's `onKeyPress` carries from the DOM
 * KeyboardEvent. RN's own type declares only `key`.
 */
export interface EnterToSendKeyData extends TextInputKeyPressEventData {
  readonly shiftKey?: boolean;
  readonly ctrlKey?: boolean;
  readonly metaKey?: boolean;
  readonly altKey?: boolean;
  readonly isComposing?: boolean;
  readonly keyCode?: number;
}

/**
 * The slice of a TextInput key-press event the handler reads.
 */
export type EnterToSendKeyPressEvent = Pick<
  NativeSyntheticEvent<EnterToSendKeyData>,
  "nativeEvent" | "preventDefault"
>;

/** Safari reports keydowns that belong to an IME composition with this code. */
const IME_COMPOSITION_KEY_CODE = 229;

/**
 * Whether a key press should send the message rather than edit the text.
 * @param nativeEvent - Key fields of the key press
 * @returns True for a bare Enter; false for Shift+Enter (newline), Ctrl/Cmd/
 * Alt+Enter (left to the browser and OS), an Enter that commits an IME
 * composition, and every other key
 */
export const isEnterToSend = (nativeEvent: EnterToSendKeyData): boolean =>
  nativeEvent.key === "Enter" &&
  !nativeEvent.shiftKey &&
  !nativeEvent.ctrlKey &&
  !nativeEvent.metaKey &&
  !nativeEvent.altKey &&
  !nativeEvent.isComposing &&
  nativeEvent.keyCode !== IME_COMPOSITION_KEY_CODE;

/**
 * Handles one TextInput key press: on web, a bare Enter sends.
 * @param event - The key-press event
 * @param onEnter - Sends the current draft; it owns the "is there anything to
 * send, and may we send it" checks, exactly as the Send button's path does
 * @remarks Called from inside the input's `onKeyPress` rather than building
 * the handler during render, so a caller's send may read refs (to keep the
 * input focused) without the read happening at render time.
 */
export const sendOnEnter = (
  event: EnterToSendKeyPressEvent,
  onEnter: () => void
): void => {
  if (Platform.OS !== "web" || !isEnterToSend(event.nativeEvent)) return;
  event.preventDefault();
  onEnter();
};
